import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserMd } from 'react-icons/fa';
import API_BASE_URL from '../config';
// Use CSS modules for isolated styling
import pdlSearchStyles from '../css-isolated/jo_search_pdl.module.css';
import modalStyles from '../css-isolated/jo_modal_pdl.module.css';
import ChangePasswordModal from './change_pass';
import authFetch from '../utils/authFetch';

function PdlHealthRecordSearch() {
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [userData, setUserData] = useState(null);
  const [remarksFilter, setRemarksFilter] = useState('not_archived');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [pendingTheme, setPendingTheme] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchData, setSearchData] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    akaNickname: ''
  });

  // Filtering & Pagination
  const ITEMS_PER_PAGE = 10;
  const filteredResults = React.useMemo(() => {
    switch (remarksFilter) {
      case 'all':
        return searchResults;
      case 'Release':
      case 'Transfer':
      case 'Death':
        return searchResults.filter(r => (r.final_remarks || '').toLowerCase() === remarksFilter.toLowerCase());
      case 'not_archived':
      default:
        return searchResults.filter(r => !r.final_remarks);
    }
  }, [searchResults, remarksFilter]);

  // Reset to page 1 when search results or filter change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchResults, remarksFilter]);

  // Helper function to calculate age from date of birth
  const calculateAge = React.useCallback((dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    
    if (isNaN(birthDate.getTime())) return 'N/A';
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age >= 0 ? age : 'N/A';
  }, []);

  // Sorting logic
  const sortedResults = React.useMemo(() => {
    if (!sortColumn) return filteredResults;

    const sorted = [...filteredResults].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'lastName':
          aValue = (a.last_name || '').toLowerCase();
          bValue = (b.last_name || '').toLowerCase();
          break;
        case 'firstName':
          aValue = (a.first_name || '').toLowerCase();
          bValue = (b.first_name || '').toLowerCase();
          break;
        case 'middleName':
          aValue = (a.middle_name || '').toLowerCase();
          bValue = (b.middle_name || '').toLowerCase();
          break;
        case 'aka':
          aValue = (a.alias_nickname || '').toLowerCase();
          bValue = (b.alias_nickname || '').toLowerCase();
          break;
        case 'age':
          const ageA = calculateAge(a.date_of_birth);
          const ageB = calculateAge(b.date_of_birth);
          aValue = ageA === 'N/A' ? 999999 : parseInt(ageA);
          bValue = ageB === 'N/A' ? 999999 : parseInt(ageB);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredResults, sortColumn, sortDirection, calculateAge]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortTooltip = (column) => {
    if (sortColumn !== column) return 'Sort A to Z';
    return sortDirection === 'asc' ? 'Sort Z to A' : 'Sort A to Z';
  };

  const renderSortIndicator = (column) => {
    if (sortColumn !== column) {
      return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.3, marginLeft: '4px' }}>
          <path d="M7 10L12 5L17 10M7 14L12 19L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '4px' }}>
        <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ) : (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '4px' }}>
        <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  };

  // Pagination - must be after sortedResults is defined
  const totalPages = Math.ceil(sortedResults.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPageData = sortedResults.slice(startIndex, endIndex);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Load user data from localStorage
  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUserData(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Update time every second
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleDarkMode = (newMode) => {
    setPendingTheme(newMode);
    setShowThemeModal(true);
  };

  const confirmThemeChange = async () => {
    // Close modal immediately
    setShowThemeModal(false);
    
    // Update local state and storage
    setIsDarkMode(pendingTheme);
    localStorage.setItem('theme', pendingTheme ? 'dark' : 'light');
    
    try {
      // Update database in background
      if (userData && userData.id) {
        await authFetch(`${API_BASE_URL}/api/update-theme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userData.id,
            is_darkmode: pendingTheme ? 1 : 0
          })
        });
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    } finally {
      setPendingTheme(null);
    }
  };

  const cancelThemeChange = () => {
    setShowThemeModal(false);
    setPendingTheme(null);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    // Preserve theme before clearing storage
    const currentTheme = localStorage.getItem('theme');
    
    localStorage.clear();
    sessionStorage.clear();
    
    // Restore theme
    if (currentTheme) {
      localStorage.setItem('theme', currentTheme);
    }
    
    setShowLogoutModal(false);
    navigate('/login-jailofficers', { replace: true });
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(`.${pdlSearchStyles.profileSection}`)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams(searchData).toString();
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/search?${queryParams}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = async (record) => {
    setShowViewModal(true);
    setIsModalLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/${record.id}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setSelectedRecord(data);
    } catch (error) {
      console.error('Error fetching record details:', error);
    } finally {
      setIsModalLoading(false);
    }
  };

  const closeModal = () => {
    setShowViewModal(false);
    setSelectedRecord(null);
    setCurrentPage(1);
  };

  // Removed update handler as Update action is no longer available


  const handleExportToPDF = () => {
    if (!selectedRecord) return;
    
    try {
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow pop-ups for this site to export PDF');
        return;
      }

      // Create the filename with the specified format
      const lastName = selectedRecord.last_name || '';
      const firstName = selectedRecord.first_name || '';
      const middleName = selectedRecord.middle_name || '';
      const aka = selectedRecord.alias_nickname || '';
      
      let filename = `PDL Health Record - ${lastName}, ${firstName}`;
      if (middleName) {
        filename += ` ${middleName}`;
      }
      if (aka) {
        filename += ` aka "${aka}"`;
      }

      // Generate HTML content for the PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${filename}</title>
          <style>
            @page {
              size: A4;
              margin: 8mm;
            }
            * {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body { 
              font-family: Arial, sans-serif; 
              margin: 0;
              padding: 8px;
              color: #000;
              font-size: 9px;
              line-height: 1.2;
            }
            input[type="checkbox"] {
              width: 12px;
              height: 12px;
              cursor: default;
              appearance: none;
              -webkit-appearance: none;
              border: 1.5px solid #000;
              border-radius: 1px;
              position: relative;
              background-color: #fff;
              display: inline-block;
              vertical-align: middle;
            }
            input[type="checkbox"]:checked {
              background-color: #000;
              border-color: #000;
            }
            input[type="checkbox"]:checked::after {
              content: '✓';
              position: absolute;
              color: #fff;
              font-size: 10px;
              font-weight: bold;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
            }
            table {
              width: 100%;
              border-collapse: collapse;
              border: 1px solid #000;
              font-size: 9px;
            }
            td {
              border: 1px solid #000;
              padding: 4px 6px;
              vertical-align: top;
            }
            @media print {
              body { margin: 0; padding: 8px; }
            }
          </style>
        </head>
        <body>
          <!-- Header Section -->
          <div style="display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 8px; gap: 8px;">
            <div style="display: flex; gap: 8px; align-items: center; flex: 1;">
              <div style="width: 60px; height: 60px; border: 1.5px solid #000; border-radius: 50%; display: flex; align-items: center; justify-content: center; overflow: hidden; background: #fff; flex-shrink: 0;">
                <img src="/BJMPRO-CAR.png" alt="BJMP Logo" style="width: 56px; height: 56px; object-fit: contain;" />
              </div>
              <div style="padding-top: 4px;">
                <div style="font-size: 9px; margin-bottom: 1px; letter-spacing: 0.3px;">REPUBLIC OF THE PHILIPPINES</div>
                <div style="font-size: 9px; margin-bottom: 1px; letter-spacing: 0.3px;">DEPARTMENT OF INTERIOR AND LOCAL GOVERNMENT</div>
                <div style="font-size: 10px; font-weight: bold; letter-spacing: 0.3px;">BUREAU OF JAIL MANAGEMENT AND PENOLOGY</div>
              </div>
            </div>
            <div style="text-align: right; padding-top: 2px; min-width: 220px; flex-shrink: 0;">
              <div style="font-size: 10px; margin-bottom: 12px;">Form No. <span style="border-bottom: 1px solid #000; display: inline-block; width: 70px; margin-left: 3px;"></span></div>
              <div style="display: flex; gap: 6px; justify-content: flex-end; font-size: 9px;">
                <span>Final Remarks:</span>
                <label style="display: flex; align-items: center; gap: 3px;">
                  <input type="checkbox" ${selectedRecord?.final_remarks === 'Release' ? 'checked' : ''} disabled />
                  <span>Release</span>
                </label>
                <label style="display: flex; align-items: center; gap: 3px;">
                  <input type="checkbox" ${selectedRecord?.final_remarks === 'Transfer' ? 'checked' : ''} disabled />
                  <span>Transfer</span>
                </label>
                <label style="display: flex; align-items: center; gap: 3px;">
                  <input type="checkbox" ${selectedRecord?.final_remarks === 'Death' ? 'checked' : ''} disabled />
                  <span>Death</span>
                </label>
              </div>
            </div>
          </div>

          <!-- Black Header Bar -->
          <div style="background: #000; color: #fff; padding: 4px 8px; margin-bottom: 0; display: flex; justify-content: space-between; align-items: center;">
            <div style="font-size: 10px; font-weight: bold; letter-spacing: 0.3px;">PDL HEALTH RECORD</div>
            <div style="font-size: 8px; font-style: italic; letter-spacing: 0.2px;">CONFIDENTIAL - Authorized Health Personnel only.</div>
          </div>

          <!-- Form Table -->
          <table>
            <tbody>
              <!-- Name Row -->
              <tr>
                <td colspan="7">
                  <div style="display: flex; gap: 6px; align-items: flex-start;">
                    <strong style="min-width: 35px; font-size: 8px;">Name:</strong>
                    <div style="flex: 1; display: grid; grid-template-columns: 1fr 1fr 1fr 0.7fr; gap: 8px;">
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 14px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.last_name || ''}</div>
                        <div style="font-size: 7px; color: #666;">Surname</div>
                      </div>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 14px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.first_name || ''}</div>
                        <div style="font-size: 7px; color: #666;">First Name</div>
                      </div>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 14px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.middle_name || ''}</div>
                        <div style="font-size: 7px; color: #666;">Middle Name</div>
                      </div>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 14px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.alias_nickname || ''}</div>
                        <div style="font-size: 7px; color: #666;">A.K.A.</div>
                      </div>
                    </div>
                  </div>
                </td>
                <td rowspan="5" style="padding: 4px; vertical-align: middle; text-align: center; width: 100px; background: #f9f9f9;">
                  <div style="font-size: 7px; font-style: italic; color: #666; margin-bottom: 3px;">(photo of PDL)</div>
                  <div style="width: 90px; height: 90px; border: 1px solid #ccc; margin: 0 auto; display: flex; align-items: center; justify-content: center; background: #fff; overflow: hidden;">
                    ${selectedRecord?.photo_of_pdl ? 
                      `<img src="${API_BASE_URL}/api/storage/${selectedRecord.photo_of_pdl}" alt="PDL" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.onerror=null;this.src='/default-image.png';" />`
                      : '<img src="/default-image.png" alt="PDL" style="width: 100%; height: 100%; object-fit: cover;" />'}
                  </div>
                </td>
              </tr>

              <!-- Sex, Age, Civil Status, Educational Attainment Row -->
              <tr>
                <td style="width: 15%;">
                  <div style="display: flex; align-items: center; gap: 4px; font-size: 8px;">
                    <strong>Sex:</strong>
                    <label style="display: flex; align-items: center; gap: 2px;">
                      <input type="checkbox" ${selectedRecord?.sex === 'M' ? 'checked' : ''} disabled />
                      <span>M</span>
                    </label>
                    <label style="display: flex; align-items: center; gap: 2px; margin-left: 3px;">
                      <input type="checkbox" ${selectedRecord?.sex === 'F' ? 'checked' : ''} disabled />
                      <span>F</span>
                    </label>
                  </div>
                </td>
                <td style="width: 10%;">
                  <div style="font-size: 8px;"><strong>Age:</strong> ${calculateAge(selectedRecord.date_of_birth)}</div>
                </td>
                <td style="width: 25%;">
                  <div style="font-size: 8px;"><strong>Civil status:</strong> ${selectedRecord?.civil_status || ''}</div>
                  <div style="font-size: 7px; color: #666;">(single/married/widow/others)</div>
                </td>
                <td colspan="4">
                  <div style="font-size: 8px;"><strong>Educational attainment:</strong> ${selectedRecord?.educ_attainment || ''}</div>
                  <div style="font-size: 7px; color: #666;">(grade/year/degree)</div>
                </td>
              </tr>

              <!-- Date of Birth, Place of Birth Row -->
              <tr>
                <td colspan="2" style="vertical-align: middle;">
                  <div style="display: flex; gap: 4px; align-items: center;">
                    <strong style="font-size: 8px; white-space: nowrap;">Date of birth:</strong>
                    <div style="display: flex; gap: 2px; align-items: center;">
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 12px; min-width: 18px; font-size: 8px; margin-bottom: 1px;">
                          ${selectedRecord?.date_of_birth ? String(new Date(selectedRecord.date_of_birth).getMonth() + 1).padStart(2, '0') : ''}
                        </div>
                        <div style="font-size: 6px; color: #666;">MM</div>
                      </div>
                      <span style="font-size: 8px; padding-bottom: 8px;">/</span>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 12px; min-width: 18px; font-size: 8px; margin-bottom: 1px;">
                          ${selectedRecord?.date_of_birth ? String(new Date(selectedRecord.date_of_birth).getDate()).padStart(2, '0') : ''}
                        </div>
                        <div style="font-size: 6px; color: #666;">DD</div>
                      </div>
                      <span style="font-size: 8px; padding-bottom: 8px;">/</span>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 12px; min-width: 28px; font-size: 8px; margin-bottom: 1px;">
                          ${selectedRecord?.date_of_birth ? new Date(selectedRecord.date_of_birth).getFullYear() : ''}
                        </div>
                        <div style="font-size: 6px; color: #666;">YY</div>
                      </div>
                    </div>
                  </div>
                </td>
                <td colspan="5" style="vertical-align: middle;">
                  <div style="display: flex; gap: 6px; align-items: center;">
                    <strong style="white-space: nowrap; font-size: 8px;">Place of birth:</strong>
                    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 4px; flex: 1;">
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 12px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.pob_municipality || ''}</div>
                        <div style="font-size: 6px; color: #666;">Municipality/City</div>
                      </div>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 12px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.pob_province || ''}</div>
                        <div style="font-size: 6px; color: #666;">Province</div>
                      </div>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 12px; margin-bottom: 1px; font-size: 8px;">${selectedRecord?.pob_region || ''}</div>
                        <div style="font-size: 8px; color: #666;">Region</div>
                      </div>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 18px; margin-bottom: 2px; font-size: 10px;">${selectedRecord?.pob_country || ''}</div>
                        <div style="font-size: 8px; color: #666;">Country</div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>

              <!-- Date of Commitment, Name of Jail Row -->
              <tr>
                <td colspan="2" style="vertical-align: middle;">
                  <div style="display: flex; gap: 8px; align-items: center;">
                    <strong style="font-size: 10px; white-space: nowrap;">Date of commitment:</strong>
                    <div style="display: flex; gap: 4px; align-items: center;">
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 18px; min-width: 25px; font-size: 10px; margin-bottom: 2px;">
                          ${selectedRecord?.date_of_commitment ? String(new Date(selectedRecord.date_of_commitment).getMonth() + 1).padStart(2, '0') : ''}
                        </div>
                        <div style="font-size: 8px; color: #666;">MM</div>
                      </div>
                      <span style="font-size: 10px; padding-bottom: 12px;">/</span>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 18px; min-width: 25px; font-size: 10px; margin-bottom: 2px;">
                          ${selectedRecord?.date_of_commitment ? String(new Date(selectedRecord.date_of_commitment).getDate()).padStart(2, '0') : ''}
                        </div>
                        <div style="font-size: 8px; color: #666;">DD</div>
                      </div>
                      <span style="font-size: 10px; padding-bottom: 12px;">/</span>
                      <div style="text-align: center;">
                        <div style="border-bottom: 1px solid #000; min-height: 18px; min-width: 35px; font-size: 10px; margin-bottom: 2px;">
                          ${selectedRecord?.date_of_commitment ? new Date(selectedRecord.date_of_commitment).getFullYear() : ''}
                        </div>
                        <div style="font-size: 8px; color: #666;">YY</div>
                      </div>
                    </div>
                  </div>
                </td>
                <td colspan="5">
                  <div><strong>Name of jail:</strong> ${selectedRecord?.name_of_jail || ''}</div>
                </td>
              </tr>

              <!-- Origin Lock-up, PhilHealth No Row -->
              <tr>
                <td colspan="2" style="font-size: 8px;">
                  <div><strong>Origin lock-up:</strong> ${selectedRecord?.origin_lock_up || ''}</div>
                </td>
                <td colspan="6" style="font-size: 8px;">
                  <div><strong>PhilHealth No:</strong> ${selectedRecord?.philhealth_no || ''}</div>
                </td>
              </tr>

              <!-- Case, Case No Row -->
              <tr>
                <td colspan="4" style="font-size: 8px;">
                  <div><strong>Case:</strong> ${selectedRecord?.case_ || ''}</div>
                </td>
                <td colspan="4" style="font-size: 8px;">
                  <div><strong>Case No:</strong> ${selectedRecord?.case_no || ''}</div>
                </td>
              </tr>

              <!-- Name of contact person, Relationship Row -->
              <tr>
                <td colspan="4" style="font-size: 8px;">
                  <div><strong>Name of contact person:</strong> ${selectedRecord?.name_of_contact_person || ''}</div>
                </td>
                <td colspan="4" style="font-size: 8px;">
                  <div><strong>Relationship:</strong> ${selectedRecord?.relationship || ''}</div>
                </td>
              </tr>

              <!-- Address of contact person, Phone/Email Row -->
              <tr>
                <td colspan="4" style="font-size: 8px;">
                  <div><strong>Address of contact person:</strong> ${selectedRecord?.add_of_contact_person || ''}</div>
                </td>
                <td colspan="4" style="font-size: 8px;">
                  <div><strong>Phone / Email:</strong> ${selectedRecord?.phone_email || ''}</div>
                </td>
              </tr>

            </tbody>
          </table>
        </body>
        </html>
      `;

      // Write content to the new window
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load then trigger print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);

    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  return (
    <div className={`${pdlSearchStyles.systemApp} ${isDarkMode ? pdlSearchStyles.darkMode : pdlSearchStyles.lightMode}`}>
      {/* System Header */}
      <header className={pdlSearchStyles.systemHeader}>
        <div className={pdlSearchStyles.headerLeft}>
          <div className={pdlSearchStyles.systemLogo}>
            <img src="/BJMPRO-CAR.png" alt="BJMP CAR Logo" className={pdlSearchStyles.systemLogoImg} />
            <div className={pdlSearchStyles.systemTitle}>
              <h1>MediCast: BJMP Health Monitoring System</h1>
              <p>Bureau of Jail Management and Penology</p>
            </div>
          </div>
        </div>
        <div className={pdlSearchStyles.headerRight}>
          <div className={pdlSearchStyles.systemInfo}>
            <div className={pdlSearchStyles.systemTime}>{currentTime.toLocaleTimeString()}</div>
            <div className={pdlSearchStyles.systemUser}>{formatDate(currentTime)}</div>
          </div>
          <div className={pdlSearchStyles.profileSection}>
            <div className={pdlSearchStyles.profileIcon} onClick={toggleDropdown}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16 14 12 14Z" fill="currentColor"/>
              </svg>
            </div>
            {isDropdownOpen && (
              <div className={pdlSearchStyles.profileDropdown}>
                <div className={pdlSearchStyles.dropdownHeader}>
                  <div className={pdlSearchStyles.userInfo}>
                    <div className={pdlSearchStyles.userRole}>
                      {userData?.user_type === 'unit_nurse' ? 'Unit Nurse' : 
                       userData?.user_type === 'chief_nurse' ? 'Chief Nurse' : 
                       userData?.user_type === 'warden' ? 'Warden' : 
                       userData?.user_type === 'admin' ? 'System Admin' : 
                       userData?.user_type === 'jail_officer' ? 'Jail Officer' : 'User'}
                    </div>
                    <div className={pdlSearchStyles.userName}>
                      {userData ? 
                        `${userData.last_name ? userData.last_name.charAt(0).toUpperCase() + userData.last_name.slice(1).toLowerCase() : ''}, ${userData.first_name ? userData.first_name.charAt(0).toUpperCase() + userData.first_name.slice(1).toLowerCase() : ''} ${userData.middle_name ? userData.middle_name.charAt(0).toUpperCase() + userData.middle_name.slice(1).toLowerCase() : ''}`.trim() 
                        : 'User Name'}
                    </div>
                  </div>
                </div>
                <div className={pdlSearchStyles.dropdownDivider}></div>
                <button className={pdlSearchStyles.dropdownItem} onClick={() => toggleDarkMode(!isDarkMode)}>
                  {isDarkMode ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" fill="currentColor"/>
                    </svg>
                  )}
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
                <button className={pdlSearchStyles.dropdownItem} onClick={() => setShowChangePasswordModal(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Change Password
                </button>
                <div className={pdlSearchStyles.dropdownDivider}></div>
                <button className={pdlSearchStyles.dropdownItem} onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className={pdlSearchStyles.jbrSearchPage}>
        {/* Header with home button */}
        <div className={pdlSearchStyles.jbrHeader}>
          <button onClick={() => navigate('/')} className={pdlSearchStyles.jbrBackBtn}>← Back to Dashboard</button>
          <h1 className={pdlSearchStyles.jbrTitle}>Search PDL Health Records</h1>
        </div>

      {/* Search Form */}
      <div className={pdlSearchStyles.searchCard}>
        <div className={pdlSearchStyles.searchHeader}>
          <div className={pdlSearchStyles.searchHeaderIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className={pdlSearchStyles.searchHeaderText}>
            <h2>Search PDL Health Records</h2>
            <p>Enter patient information to find health records</p>
          </div>
        </div>
          
        
        <form onSubmit={handleSearch} className={pdlSearchStyles.searchForm}>
          <div className={pdlSearchStyles.searchFormGrid}>
            <div className={pdlSearchStyles.inputGroup}>
              <div className={pdlSearchStyles.inputIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={pdlSearchStyles.inputWrapper}>
                <label className={pdlSearchStyles.inputLabel}>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={searchData.lastName}
                  onChange={handleInputChange}
                  className={pdlSearchStyles.searchInput}
                  placeholder="Enter last name"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className={pdlSearchStyles.inputGroup}>
              <div className={pdlSearchStyles.inputIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={pdlSearchStyles.inputWrapper}>
                <label className={pdlSearchStyles.inputLabel}>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={searchData.firstName}
                  onChange={handleInputChange}
                  className={pdlSearchStyles.searchInput}
                  placeholder="Enter first name"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className={pdlSearchStyles.inputGroup}>
              <div className={pdlSearchStyles.inputIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={pdlSearchStyles.inputWrapper}>
                <label className={pdlSearchStyles.inputLabel}>Middle Name</label>
                <input
                  type="text"
                  name="middleName"
                  value={searchData.middleName}
                  onChange={handleInputChange}
                  className={pdlSearchStyles.searchInput}
                  placeholder="Enter middle name"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className={pdlSearchStyles.inputGroup}>
              <div className={pdlSearchStyles.inputIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={pdlSearchStyles.inputWrapper}>
                <label className={pdlSearchStyles.inputLabel}>AKA/Nickname</label>
                <input
                  type="text"
                  name="akaNickname"
                  value={searchData.akaNickname}
                  onChange={handleInputChange}
                  className={pdlSearchStyles.searchInput}
                  placeholder="Enter AKA/nickname"
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          <div className={pdlSearchStyles.searchActions}>
            <button type="submit" className={pdlSearchStyles.searchButton} disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className={pdlSearchStyles.searchSpinner}></div>
                  Searching...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Search Records
                </>
              )}
            </button>
            
            <button 
              type="button" 
              className={pdlSearchStyles.clearButton}
              onClick={() => setSearchData({ lastName: '', firstName: '', middleName: '', akaNickname: '' })}
            >
              Clear
            </button>
          </div>
        </form>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className={pdlSearchStyles.resultsSection}>
          <div className={pdlSearchStyles.resultsHeader}>
            <div className={pdlSearchStyles.resultsHeaderIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={pdlSearchStyles.resultsHeaderText}>
              <h3>Searching...</h3>
              <p>Please wait while we search for health records</p>
            </div>
          </div>
          <div className={pdlSearchStyles.resultsContainer}>
            <div className={pdlSearchStyles.loadingState}>
              <div className={pdlSearchStyles.loadingSpinner}></div>
              <p>Searching health records...</p>
            </div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {searchResults.length > 0 && (
        <div className={pdlSearchStyles.resultsSection}>
          <div className={pdlSearchStyles.resultsHeader}>
            <div className={pdlSearchStyles.resultsHeaderIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={pdlSearchStyles.resultsHeaderText}>
              <h3>Health Records Found</h3>
              <p>Showing {filteredResults.length} of {searchResults.length} record(s)</p>
            </div>
            <div style={{ marginLeft: 'auto', minWidth: '220px' }}>
              <select
                className={pdlSearchStyles.searchInput}
                value={remarksFilter}
                onChange={(e) => setRemarksFilter(e.target.value)}
                style={{ marginTop: 0 }}
              >
                <option value="not_archived">Not Archived (No Remarks)</option>
                <option value="all">All (Archived + Not)</option>
                <option value="Release">Release</option>
                <option value="Transfer">Transfer</option>
                <option value="Death">Death</option>
              </select>
            </div>
          </div>

          <div className={pdlSearchStyles.resultsContainer}>
            <div className={pdlSearchStyles.resultsTableContainer}>
              <table className={pdlSearchStyles.resultsTable}>
                <thead>
                  <tr>
                    <th className={pdlSearchStyles.tableHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Current Cell
                      </div>
                    </th>
                    <th className={pdlSearchStyles.tableHeader} onClick={() => handleSort('lastName')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('lastName')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Last Name
                        {renderSortIndicator('lastName')}
                      </div>
                    </th>
                    <th className={pdlSearchStyles.tableHeader} onClick={() => handleSort('firstName')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('firstName')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        First Name
                        {renderSortIndicator('firstName')}
                      </div>
                    </th>
                    <th className={pdlSearchStyles.tableHeader} onClick={() => handleSort('middleName')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('middleName')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Middle Name
                        {renderSortIndicator('middleName')}
                      </div>
                    </th>
                    <th className={pdlSearchStyles.tableHeader} onClick={() => handleSort('aka')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('aka')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        AKA
                        {renderSortIndicator('aka')}
                      </div>
                    </th>
                    <th className={pdlSearchStyles.tableHeader} onClick={() => handleSort('age')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('age')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Age
                        {renderSortIndicator('age')}
                      </div>
                    </th>
                    <th className={pdlSearchStyles.tableHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPageData.map((record, index) => (
                    <tr key={record.id} className={pdlSearchStyles.tableRow}>
                      <td className={pdlSearchStyles.tableCell}>
                        <span className={`${pdlSearchStyles.cellBadge} ${record.cell ? pdlSearchStyles.cellBadgeAssigned : pdlSearchStyles.cellBadgeUnassigned}`}>
                          {record.cell?.cell_no || 'Unassigned'}
                        </span>
                      </td>
                      <td className={pdlSearchStyles.tableCell}>
                        <span className={pdlSearchStyles.cellText}>{record.last_name || 'N/A'}</span>
                      </td>
                      <td className={pdlSearchStyles.tableCell}>
                        <span className={pdlSearchStyles.cellText}>{record.first_name || 'N/A'}</span>
                      </td>
                      <td className={pdlSearchStyles.tableCell}>
                        <span className={pdlSearchStyles.cellText}>{record.middle_name || 'N/A'}</span>
                      </td>
                      <td className={pdlSearchStyles.tableCell}>
                        <span className={pdlSearchStyles.cellText}>{record.alias_nickname || 'N/A'}</span>
                      </td>
                      <td className={pdlSearchStyles.tableCell}>
                        <span className={pdlSearchStyles.cellText}>{calculateAge(record.date_of_birth)}</span>
                      </td>
                      <td className={pdlSearchStyles.tableCell}>
                        <div className={pdlSearchStyles.actionButtons}>
                          <button
                            className={`${pdlSearchStyles.actionButton} ${pdlSearchStyles.viewButton}`}
                            onClick={() => handleView(record)}
                            title="View Details"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            View
                          </button>
                          
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className={pdlSearchStyles.paginationContainer}>
                <button
                  className={pdlSearchStyles.paginationButton}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Previous
                </button>

                <div className={pdlSearchStyles.paginationInfo}>
                  <span className={pdlSearchStyles.paginationText}>
                    Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                  </span>
                  <span className={pdlSearchStyles.paginationDivider}>•</span>
                  <span className={pdlSearchStyles.paginationText}>
                    Showing <strong>{startIndex + 1}</strong> to <strong>{Math.min(endIndex, filteredResults.length)}</strong> of <strong>{filteredResults.length}</strong> records
                  </span>
                </div>

                <button
                  className={pdlSearchStyles.paginationButton}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No records after filter */}
      {!isLoading && searchResults.length > 0 && filteredResults.length === 0 && (
        <div className={pdlSearchStyles.emptyState}>
          <svg className={pdlSearchStyles.emptyStateIcon} width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 10L14 14M14 10L10 14M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h4>No Records For Filter</h4>
          <p>No records match the selected final remarks filter option.</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && searchResults.length === 0 && (
        <div className={pdlSearchStyles.emptyState}>
          <div className={pdlSearchStyles.emptyStateIcon}>
            <FaUserMd size={48} />
          </div>
          <h4>No health records found</h4>
          <p>Try adjusting your search criteria or check if the patient information is correct.</p>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && (
        <div className={`${modalStyles.jbrModalOverlay} ${!isDarkMode ? 'lightMode' : ''}`} onClick={closeModal}>
          <div className={`${modalStyles.jbrModalContent} ${!isDarkMode ? 'lightMode' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className={modalStyles.jbrModalHeader}>
              <div className={modalStyles.jbrHeaderLeft}>
                <div className={modalStyles.jbrLogo}>🏛️</div>
                <div className={modalStyles.jbrHeaderText}>
                  <h4 className={modalStyles.jbrGradientText}>Republic of the Philippines</h4>
                  <h5 className={modalStyles.jbrGradientText}>BUREAU OF JAIL MANAGEMENT AND PENOLOGY</h5>
                </div>
              </div>
              <div className={modalStyles.jbrHeaderActions}>
                <button className={modalStyles.jbrPdfBtn} onClick={handleExportToPDF} title="Save as PDF">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  PDF
                </button>
                <button className={modalStyles.jbrCloseBtn} onClick={closeModal}>&times;</button>
              </div>
            </div>

            <div className={modalStyles.jbrModalBody}>
              {isModalLoading ? (
                <div className={modalStyles.jbrLoadingContainer}>
                  <div className={modalStyles.jbrLoadingSpinner}></div>
                  <span className={modalStyles.jbrGradientText}>Loading...</span>
                </div>
              ) : selectedRecord && (
                <>
                  <h2 className={modalStyles.jbrFormTitle}>
                    <span className={modalStyles.jbrInteractiveIcon}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17ZM17 21V10L13 6H7V19H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                    PDL Health Record
                  </h2>

                  <style>
                    {`
                      #jo-form-container input[type="checkbox"] {
                        width: 16px;
                        height: 16px;
                        cursor: default;
                        appearance: none;
                        -webkit-appearance: none;
                        border: 2px solid #000;
                        border-radius: 2px;
                        position: relative;
                        background-color: #fff;
                      }
                      #jo-form-container input[type="checkbox"]:checked {
                        background-color: #000;
                        border-color: #000;
                      }
                      #jo-form-container input[type="checkbox"]:checked::after {
                        content: '✓';
                        position: absolute;
                        color: #fff;
                        font-size: 14px;
                        font-weight: bold;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                      }
                    `}
                  </style>
                  <div id="jo-form-container" style={{ 
                    maxWidth: '1100px', 
                    margin: '0 auto 1.5rem auto', 
                    background: '#ffffff', 
                    padding: '20px', 
                    fontFamily: 'Arial, sans-serif',
                    color: '#000000',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '1px solid #ddd'
                  }}>
                    
                    {/* Header Section */}
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      marginBottom: '20px',
                      gap: '15px'
                    }}>
                      {/* Logo and Header Text */}
                      <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flex: 1 }}>
                        <div style={{ 
                          width: '100px',
                          height: '100px',
                          border: '2px solid #000',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          background: '#fff',
                          flexShrink: 0
                        }}>
                          {!logoFailed ? (
                            <img 
                              src="/BJMPRO-CAR.png" 
                              alt="BJMP Logo" 
                              style={{ width: '95px', height: '95px', objectFit: 'contain' }}
                              onError={() => setLogoFailed(true)}
                            />
                          ) : (
                            <div style={{ fontSize: '10px', textAlign: 'center', fontWeight: 'bold', lineHeight: 1.2 }}>BJMP<br/>LOGO</div>
                          )}
                        </div>
                        <div style={{ paddingTop: '15px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 'normal', marginBottom: '2px', letterSpacing: '0.5px' }}>
                            REPUBLIC OF THE PHILIPPINES
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: 'normal', marginBottom: '2px', letterSpacing: '0.5px' }}>
                            DEPARTMENT OF INTERIOR AND LOCAL GOVERNMENT
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '0', letterSpacing: '0.5px' }}>
                            BUREAU OF JAIL MANAGEMENT AND PENOLOGY
                          </div>
                        </div>
                      </div>
                      
                      {/* Right Side - Form No and Final Remarks */}
                      <div style={{ textAlign: 'right', paddingTop: '10px', minWidth: '300px', flexShrink: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 'normal', marginBottom: '25px' }}>
                          Form No. <span style={{ borderBottom: '1px solid #000', display: 'inline-block', width: '100px', marginLeft: '5px' }}></span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center', fontSize: '12px' }}>
                          <span style={{ marginRight: '5px' }}>Final Remarks:</span>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <input type="checkbox" checked={selectedRecord?.final_remarks === 'Release'} disabled readOnly style={{ width: '15px', height: '15px', cursor: 'default' }} />
                            <span>Release</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <input type="checkbox" checked={selectedRecord?.final_remarks === 'Transfer'} disabled readOnly style={{ width: '15px', height: '15px', cursor: 'default' }} />
                            <span>Transfer</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <input type="checkbox" checked={selectedRecord?.final_remarks === 'Death'} disabled readOnly style={{ width: '15px', height: '15px', cursor: 'default' }} />
                            <span>Death</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Black Header Bar */}
                    <div style={{ 
                      background: '#000', 
                      color: '#fff', 
                      padding: '6px 12px',
                      marginBottom: '0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                        PDL HEALTH RECORD
                      </div>
                      <div style={{ fontSize: '11px', fontStyle: 'italic', letterSpacing: '0.3px' }}>
                        CONFIDENTIAL - Authorized Health Personnel only.
                      </div>
                    </div>

                    {/* Form Table */}
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      border: '1px solid #000',
                      fontSize: '11px'
                    }}>
                      <tbody>
                        {/* Name Row */}
                        <tr>
                          <td colSpan="7" style={{ border: '1px solid #000', padding: '8px 10px', position: 'relative' }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                              <strong style={{ minWidth: '45px' }}>Name:</strong>
                              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 0.7fr', gap: '15px' }}>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '20px', marginBottom: '3px' }}>{selectedRecord?.last_name || ''}</div>
                                  <div style={{ fontSize: '9px', color: '#666' }}>Surname</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '20px', marginBottom: '3px' }}>{selectedRecord?.first_name || ''}</div>
                                  <div style={{ fontSize: '9px', color: '#666' }}>First Name</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '20px', marginBottom: '3px' }}>{selectedRecord?.middle_name || ''}</div>
                                  <div style={{ fontSize: '9px', color: '#666' }}>Middle Name</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '20px', marginBottom: '3px' }}>{selectedRecord?.alias_nickname || ''}</div>
                                  <div style={{ fontSize: '9px', color: '#666' }}>A.K.A.</div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td rowSpan="5" style={{ border: '1px solid #000', padding: '10px', verticalAlign: 'middle', textAlign: 'center', width: '170px', background: '#f9f9f9' }}>
                            <div style={{ fontSize: '9px', fontStyle: 'italic', color: '#666', marginBottom: '8px' }}>(photo of PDL)</div>
                            <div style={{ width: '140px', height: '140px', border: '1px solid #ccc', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', overflow: 'hidden' }}>
                              {selectedRecord?.photo_of_pdl ? (
                                <img 
                                  src={`${API_BASE_URL}/api/storage/${selectedRecord.photo_of_pdl}`}
                                  alt="PDL"
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={(e) => { e.target.onerror = null; e.target.src = '/default-image.png'; }}
                                />
                              ) : (
                                <img 
                                  src="/default-image.png"
                                  alt="No PDL available"
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                        
                        {/* Sex, Age, Civil Status, Educational Attainment Row */}
                        <tr>
                          <td style={{ border: '1px solid #000', padding: '8px 10px', width: '15%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <strong>Sex:</strong>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <input type="checkbox" checked={selectedRecord?.sex === 'M'} disabled readOnly style={{ width: '14px', height: '14px', margin: '0' }} />
                                <span>M</span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '3px', marginLeft: '5px' }}>
                                <input type="checkbox" checked={selectedRecord?.sex === 'F'} disabled readOnly style={{ width: '14px', height: '14px', margin: '0' }} />
                                <span>F</span>
                              </label>
                            </div>
                          </td>
                          <td style={{ border: '1px solid #000', padding: '8px 10px', width: '10%' }}>
                            <div><strong>Age:</strong> {selectedRecord ? calculateAge(selectedRecord.date_of_birth) : ''}</div>
                          </td>
                          <td style={{ border: '1px solid #000', padding: '8px 10px', width: '25%' }}>
                            <div>
                              <div style={{ marginBottom: '3px' }}><strong>Civil status:</strong> {selectedRecord?.civil_status || ''}</div>
                              <div style={{ fontSize: '9px', color: '#666' }}>(single/married/widow/others, specify)</div>
                            </div>
                          </td>
                          <td colSpan="4" style={{ border: '1px solid #000', padding: '8px 10px' }}>
                            <div>
                              <div style={{ marginBottom: '3px' }}><strong>Educational attainment:</strong> {selectedRecord?.educ_attainment || ''}</div>
                              <div style={{ fontSize: '9px', color: '#666' }}>(grade/year/degree)</div>
                            </div>
                          </td>
                        </tr>

                        {/* Date of Birth, Place of Birth Row */}
                        <tr>
                          <td colSpan="2" style={{ border: '1px solid #000', padding: '8px 10px', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <strong style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Date of birth:</strong>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '18px', minWidth: '25px', fontSize: '10px', marginBottom: '2px' }}>
                                    {selectedRecord?.date_of_birth ? String(new Date(selectedRecord.date_of_birth).getMonth() + 1).padStart(2, '0') : ''}
                                  </div>
                                  <div style={{ fontSize: '8px', color: '#666' }}>MM</div>
                                </div>
                                <span style={{ fontSize: '10px', paddingBottom: '12px' }}>/</span>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '18px', minWidth: '25px', fontSize: '10px', marginBottom: '2px' }}>
                                    {selectedRecord?.date_of_birth ? String(new Date(selectedRecord.date_of_birth).getDate()).padStart(2, '0') : ''}
                                  </div>
                                  <div style={{ fontSize: '8px', color: '#666' }}>DD</div>
                                </div>
                                <span style={{ fontSize: '10px', paddingBottom: '12px' }}>/</span>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '18px', minWidth: '35px', fontSize: '10px', marginBottom: '2px' }}>
                                    {selectedRecord?.date_of_birth ? new Date(selectedRecord.date_of_birth).getFullYear() : ''}
                                  </div>
                                  <div style={{ fontSize: '8px', color: '#666' }}>YY</div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td colSpan="5" style={{ border: '1px solid #000', padding: '8px 10px', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                              <strong style={{ whiteSpace: 'nowrap', fontSize: '11px' }}>Place of birth:</strong>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', flex: 1 }}>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '18px', marginBottom: '2px', fontSize: '10px' }}>{selectedRecord?.pob_municipality || ''}</div>
                                  <div style={{ fontSize: '8px', color: '#666' }}>Municipality/City</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '18px', marginBottom: '2px', fontSize: '10px' }}>{selectedRecord?.pob_province || ''}</div>
                                  <div style={{ fontSize: '8px', color: '#666' }}>Province</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '18px', marginBottom: '2px', fontSize: '10px' }}>{selectedRecord?.pob_region || ''}</div>
                                  <div style={{ fontSize: '8px', color: '#666' }}>Region</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '18px', marginBottom: '2px', fontSize: '10px' }}>{selectedRecord?.pob_country || ''}</div>
                                  <div style={{ fontSize: '8px', color: '#666' }}>Country</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>

                        {/* Date of Commitment, Name of Jail Row */}
                        <tr>
                          <td colSpan="2" style={{ border: '1px solid #000', padding: '8px 10px', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <strong style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Date of commitment:</strong>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '18px', minWidth: '25px', fontSize: '10px', marginBottom: '2px' }}>
                                    {selectedRecord?.date_of_commitment ? String(new Date(selectedRecord.date_of_commitment).getMonth() + 1).padStart(2, '0') : ''}
                                  </div>
                                  <div style={{ fontSize: '8px', color: '#666' }}>MM</div>
                                </div>
                                <span style={{ fontSize: '10px', paddingBottom: '12px' }}>/</span>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '18px', minWidth: '25px', fontSize: '10px', marginBottom: '2px' }}>
                                    {selectedRecord?.date_of_commitment ? String(new Date(selectedRecord.date_of_commitment).getDate()).padStart(2, '0') : ''}
                                  </div>
                                  <div style={{ fontSize: '8px', color: '#666' }}>DD</div>
                                </div>
                                <span style={{ fontSize: '10px', paddingBottom: '12px' }}>/</span>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #000', minHeight: '18px', minWidth: '35px', fontSize: '10px', marginBottom: '2px' }}>
                                    {selectedRecord?.date_of_commitment ? new Date(selectedRecord.date_of_commitment).getFullYear() : ''}
                                  </div>
                                  <div style={{ fontSize: '8px', color: '#666' }}>YY</div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td colSpan="5" style={{ border: '1px solid #000', padding: '8px 10px' }}>
                            <div><strong>Name of jail:</strong> {selectedRecord?.name_of_jail || ''}</div>
                          </td>
                        </tr>

                        {/* Origin Lock-up, PhilHealth No Row */}
                        <tr>
                          <td colSpan="2" style={{ border: '1px solid #000', padding: '8px 10px' }}>
                            <div><strong>Origin lock-up:</strong> {selectedRecord?.origin_lock_up || ''}</div>
                          </td>
                          <td colSpan="6" style={{ border: '1px solid #000', padding: '8px 10px' }}>
                            <div><strong>PhilHealth No:</strong> {selectedRecord?.philhealth_no || ''}</div>
                          </td>
                        </tr>

                        {/* Case, Case No Row */}
                        <tr>
                          <td colSpan="4" style={{ border: '1px solid #000', padding: '8px 10px' }}>
                            <div><strong>Case:</strong> {selectedRecord?.case_ || ''}</div>
                          </td>
                          <td colSpan="4" style={{ border: '1px solid #000', padding: '8px 10px' }}>
                            <div><strong>Case No:</strong> {selectedRecord?.case_no || ''}</div>
                          </td>
                        </tr>

                        {/* Name of contact person, Relationship Row */}
                        <tr>
                          <td colSpan="4" style={{ border: '1px solid #000', padding: '8px 10px' }}>
                            <div><strong>Name of contact person:</strong> {selectedRecord?.name_of_contact_person || ''}</div>
                          </td>
                          <td colSpan="4" style={{ border: '1px solid #000', padding: '8px 10px' }}>
                            <div><strong>Relationship:</strong> {selectedRecord?.relationship || ''}</div>
                          </td>
                        </tr>

                        {/* Address of contact person, Phone/Email Row */}
                        <tr>
                          <td colSpan="4" style={{ border: '1px solid #000', padding: '8px 10px' }}>
                            <div><strong>Address of contact person:</strong> {selectedRecord?.add_of_contact_person || ''}</div>
                          </td>
                          <td colSpan="4" style={{ border: '1px solid #000', padding: '8px 10px' }}>
                            <div><strong>Phone / Email:</strong> {selectedRecord?.phone_email || ''}</div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        userData={JSON.parse(localStorage.getItem('user'))}
        onSuccess={(msg) => {
          setSuccessMessage(msg);
          setShowSuccessModal(true);
        }}
        onError={(msg) => {
          setErrorMessage(msg);
          setShowErrorModal(true);
        }}
      />

      {/* Success Modal */}
      {showSuccessModal && (
        <div className={pdlSearchStyles.modalOverlay} onClick={() => setShowSuccessModal(false)}>
          <div className={pdlSearchStyles.successModal} onClick={(e) => e.stopPropagation()}>
            <div className={pdlSearchStyles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Success</h3>
            <p>{successMessage}</p>
            <button className={pdlSearchStyles.successBtn} onClick={() => setShowSuccessModal(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className={pdlSearchStyles.modalOverlay} onClick={() => setShowErrorModal(false)}>
          <div className={pdlSearchStyles.errorModal} onClick={(e) => e.stopPropagation()}>
            <div className={pdlSearchStyles.errorIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className={pdlSearchStyles.errorBtn} onClick={() => setShowErrorModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Theme Change Confirmation Modal */}
      {showThemeModal && (
        <div className={pdlSearchStyles.modalOverlay} onClick={cancelThemeChange}>
          <div className={pdlSearchStyles.modal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#f59e0b', background: isDarkMode ? '#1e293b' : '#ffffff' }}>
            <div className={pdlSearchStyles.themeIcon} style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
              {pendingTheme ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <h3 style={{ color: '#f59e0b', textAlign: 'center' }}>Change Theme</h3>
            <p style={{ textAlign: 'center', color: isDarkMode ? '#e2e8f0' : '#475569' }}>Are you sure you want to switch to <strong>{pendingTheme ? 'Dark' : 'Light'} Mode</strong>?</p>
            <div style={{ display: 'flex', gap: '1rem', width: '100%' }}>
              <button className={pdlSearchStyles.cancelBtn} onClick={cancelThemeChange}>
                Cancel
              </button>
              <button className={pdlSearchStyles.themeBtn} onClick={confirmThemeChange}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className={pdlSearchStyles.modalOverlay} onClick={cancelLogout}>
          <div className={pdlSearchStyles.modal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#ef4444' }}>
            <div className={pdlSearchStyles.themeIcon} style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="16,17 21,12 16,7" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="21" y1="12" x2="9" y2="12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 style={{ color: '#ef4444' }}>Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className={pdlSearchStyles.cancelBtn} onClick={cancelLogout}>Cancel</button>
              <button className={pdlSearchStyles.themeBtn} onClick={confirmLogout} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>Logout</button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
}

export default PdlHealthRecordSearch;
